import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import {
  EFormBuilderFieldType,
  TFormBuilderField,
  TFormBuilderEditSchemaCamel,
} from '@/types';

/**
 * Hook for managing form builder fields with operations for adding, removing, reordering and updating fields
 * @param field - Optional field to operate on for field-specific operations
 * @returns Object containing fields array and field manipulation functions
 */
export const useFormBuilderFields = <T extends TFormBuilderField>(
  field?: T,
) => {
  const { watch, setValue, getValues } =
    useFormContext<TFormBuilderEditSchemaCamel>();

  const createFieldByType = useCallback(
    (value: EFormBuilderFieldType): TFormBuilderField => {
      const fieldId = uuidv4();
      switch (value) {
        case EFormBuilderFieldType.CHECKBOX:
          return {
            id: fieldId,
            type: EFormBuilderFieldType.CHECKBOX,
            label: '',
            options: [{ id: uuidv4(), value: '' }],
            required: false,
          };
        case EFormBuilderFieldType.DATE:
          return {
            id: fieldId,
            type: EFormBuilderFieldType.DATE,
            label: '',
            required: false,
          };
        case EFormBuilderFieldType.DROPDOWN:
          return {
            id: fieldId,
            type: EFormBuilderFieldType.DROPDOWN,
            label: '',
            placeholder: '',
            options: [
              { id: uuidv4(), value: '' },
              { id: uuidv4(), value: '' },
            ],
            required: false,
          };
        case EFormBuilderFieldType.FILE:
          return {
            id: fieldId,
            type: EFormBuilderFieldType.FILE,
            label: '',
            required: false,
          };
        case EFormBuilderFieldType.MULTIPLE_CHOICE:
          return {
            id: fieldId,
            type: EFormBuilderFieldType.MULTIPLE_CHOICE,
            label: '',
            options: [
              { id: uuidv4(), value: '' },
              { id: uuidv4(), value: '' },
            ],
            required: false,
          };
        case EFormBuilderFieldType.MULTILINE_INPUT:
          return {
            id: fieldId,
            type: EFormBuilderFieldType.MULTILINE_INPUT,
            label: '',
            placeholder: '',
            required: false,
          };
        case EFormBuilderFieldType.TEXT_EDITOR:
          return {
            id: fieldId,
            type: EFormBuilderFieldType.TEXT_EDITOR,
            label: '',
            placeholder: '',
            required: false,
          };
        case EFormBuilderFieldType.SINGLE_CHOICE:
          return {
            id: fieldId,
            type: EFormBuilderFieldType.SINGLE_CHOICE,
            label: '',
            options: [
              { id: uuidv4(), value: '' },
              { id: uuidv4(), value: '' },
            ],
            required: false,
          };
        case EFormBuilderFieldType.SINGLE_LINE_INPUT:
          return {
            id: fieldId,
            type: EFormBuilderFieldType.SINGLE_LINE_INPUT,
            label: '',
            placeholder: '',
            required: false,
          };
        case EFormBuilderFieldType.TEXT:
          return {
            id: fieldId,
            type: EFormBuilderFieldType.TEXT,
            text: '',
          };
        default:
          throw new Error('Bad format');
      }
    },
    [],
  );

  const addField = useCallback(
    (value: EFormBuilderFieldType) => {
      const field = createFieldByType(value);
      const currentFields = getValues('fields') || [];
      setValue('fields', [...currentFields, field]);
    },
    [createFieldByType, getValues, setValue],
  );

  const removeField = useCallback(() => {
    if (!field?.id) throw new Error('field is not found');
    const currentFields = getValues('fields') || [];
    const newFields = currentFields.filter(({ id }) => field.id !== id);
    setValue('fields', newFields, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [field?.id, getValues, setValue]);

  const copyField = useCallback(() => {
    if (!field) throw new Error('field is not found');
    const newField: TFormBuilderField = {
      ...field,
      id: uuidv4(),
    };
    const currentFields = getValues('fields') || [];
    setValue('fields', [...currentFields, newField]);
  }, [field, getValues, setValue]);

  const moveDownField = useCallback(() => {
    if (!field?.id) throw new Error('field is not found');
    const index = getValues('fields').findIndex(({ id }) => id === field.id);
    if (index < 0 || index === getValues('fields').length - 1) return;

    const updated = [...getValues('fields')];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setValue('fields', updated, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [field?.id, getValues, setValue]);

  const moveUpField = useCallback(() => {
    if (!field?.id) throw new Error('field is not found');
    const index = getValues('fields').findIndex(({ id }) => id === field.id);

    if (index <= 0) return;

    const updated = [...getValues('fields')];

    [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];
    setValue('fields', updated, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [field?.id, getValues, setValue]);

  const changeFields = useCallback(
    (value: Partial<T>) => {
      if (!field?.id) throw new Error('field is not found');
      const index = getValues('fields').findIndex(({ id }) => field.id === id);
      if (index === -1) return;
      setValue(
        `fields.${index}`,
        { ...field, ...value },
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
    },
    [field, getValues, setValue],
  );

  return {
    fields: watch('fields'),
    setValue,
    addField,
    removeField,
    copyField,
    moveUpField,
    moveDownField,
    changeFields,
  };
};
